import { LoadingButton } from '@mui/lab';
import { Button, Stack, Typography } from '@mui/material';
import { EmailTemplateResult } from 'api/client-domain/email-template-service/email-template-service.types';
import IncDrawer, {
  IncDrawerType,
} from 'components/drawers/inc-drawer/inc-drawer.component';
import UtilityDrawerBody from 'components/drawers/utility-drawer/utility-drawer-body/utility-drawer-body.component';
import UtilityDrawerFooter from 'components/drawers/utility-drawer/utility-drawer-footer/utility-drawer-footer.component';
import EditableTypographyField from 'components/forms/fields/editable-typography-field/editable-typography-field.component';
import IncForm from 'components/forms/inc-form/inc-form.component';
import { MutableRefObject } from 'react';
import EmailEditor, {
  EditorRef,
  EmailEditorProps,
  UnlayerOptions,
} from 'react-email-editor';
import { FieldValues, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { getClientAlias } from 'util/client-alias';
import { getClientId } from 'util/client-id';
import { getEnv } from 'util/getEnv';
import { Option } from 'util/multi-select';
import { validate } from './email-editor-drawer.utils';
import TagErrors from './tag-errors.component';

export type EmailEditorDrawerDataType = {
  template: Partial<EmailTemplateResult>;
  setFormFieldValue?: (option: Option) => void;
};

export type EmailEditorDrawerProps = {
  drawerProps: IncDrawerType<EmailEditorDrawerDataType>;
  editorProps: {
    ref: MutableRefObject<EditorRef>;
    onReady: () => void;
  };
  submit: () => Promise<void>;
  submitLoading: boolean;
  isLoading: boolean;
  themeType: EmailEditorProps['options']['appearance']['theme'];
  form: UseFormReturn<FieldValues, any>;
  error: ReturnType<typeof validate> | null;
  mergeTags: UnlayerOptions['mergeTags'];
  specialLinks: UnlayerOptions['specialLinks'];
};

export const EmailEditorDrawer = ({
  drawerProps,
  editorProps,
  submit,
  submitLoading,
  isLoading,
  themeType,
  form,
  error,
  mergeTags,
  specialLinks,
}: EmailEditorDrawerProps) => {
  const { t } = useTranslation();

  return (
    <IncForm formMethods={form}>
      <IncDrawer
        {...drawerProps}
        data-testid="EmailEditorDrawer.Drawer"
        title={
          <EditableTypographyField
            name="name"
            variant="h1"
            maxWidth={800}
            disabled={drawerProps.data?.template?.emailTemplateType === 'EVENT'}
          />
        }
        subtitle={
          <Stack
            direction="row"
            spacing={1}
            maxWidth={400}
            alignItems="baseline"
          >
            <Typography px={1}>
              {t('drawer.emailEditorDrawer.subject.label')}
            </Typography>
            <EditableTypographyField
              name="subject"
              variant="h5"
              disabled={
                drawerProps.data?.template?.emailTemplateType === 'EVENT'
              }
            />
          </Stack>
        }
        isLoading={isLoading}
        disableBackdropClose
      >
        <UtilityDrawerBody>
          <EmailEditor
            {...editorProps}
            minHeight="100%"
            options={{
              projectId: getEnv('REACT_APP_UNLAYER_PROJECT_ID'),
              user: {
                id: getClientId(),
                name: getClientAlias(),
              },
              id: 'editor-container',
              displayMode: 'email',
              appearance: {
                // @ts-ignore
                loader: {
                  html: '</>',
                },
                theme: themeType,
                panels: {
                  tools: {
                    dock: 'right',
                    collapsible: false,
                  },
                },
              },
              mergeTags,
              specialLinks,
              // Unlayer enables all AI features by default. Keep this explicit --
              // see sc-287471: a previous features.ai config was silently ignored
              // by Unlayer, so verify in the builder after any editor change.
              features: {
                ai: {
                  enabled: false,
                  assistant: false,
                },
              },
            }}
          />
        </UtilityDrawerBody>
        <UtilityDrawerFooter>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            justifyContent="flex-end"
            alignItems="center"
          >
            <TagErrors error={error} />
            <LoadingButton
              type="button"
              variant="contained"
              onClick={() => {
                submit();
              }}
              sx={{ minWidth: 150, width: { xs: '100%', md: 'auto' } }}
              loading={submitLoading}
              data-testid="EmailEditorDrawer.SubmitButton"
            >
              {t('drawer.emailEditorDrawer.buttons.submitBtn')}
            </LoadingButton>
            <Button
              variant="outlined"
              data-testid="EmailEditorDrawer.CancelButton"
              sx={{ minWidth: 150, width: { xs: '100%', md: 'auto' } }}
              onClick={() => drawerProps.close()}
            >
              {t('forms.buttons.cancel')}
            </Button>
          </Stack>
        </UtilityDrawerFooter>
      </IncDrawer>
    </IncForm>
  );
};

export default EmailEditorDrawer;
